import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { Spinner } from '@thefarmersfront/kitchen';

import TagManager from '@sooro-io/react-gtm-module';
import { useShallow } from 'zustand/react/shallow';

import { ReactTooltip } from './components/common';
import { IS_DEV_ENV } from './components/common/DevToolsFab';
import { Layout, LoginLayout } from './components/layout';
import ChangePasswordPage from './pages/auth/changePassword/ChangePasswordPage';
import FindIdPage from './pages/auth/findId/FindIdPage';
import FindPasswordPage from './pages/auth/findPassword/FindPasswordPage';
import TwoFactorAuthPage from './pages/auth/twoFactorAuth/TwoFactorAuthPage';
import DeliveryAreaRefinePage from './pages/delivery-area/refine/DeliveryAreaRefinePage';
import LoginPage from './pages/login/LoginPage';
import LogoutPage from './pages/logout/LogoutPage';

import { useAuthState, useAuthSync } from './hooks/auth';
import { useGlobalLoading, useTwcChatbot } from './hooks/common';
import { useUserInfoStore } from './store';
import { getDecodedJwtValue } from './utils';

import { getAccessibleRoutes, getDefaultRoute } from './configs';
import { PATH, PUBLIC_PATHS } from './constants/path';

const DevToolsFab = IS_DEV_ENV ? lazy(() => import('./components/common/DevToolsFab/DevToolsFab')) : null;

const LoadingComponent = ({ isLoading, dimmed }: { isLoading: boolean; dimmed: boolean }) => {
  if (!isLoading) {
    return null;
  }

  return <Spinner fullscreen={dimmed ? true : { dimmed: false }} />;
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading, dimmed } = useGlobalLoading();

  const { isAuthenticated } = useAuthState();

  useAuthSync();

  const [userInfo, setUserInfo, resetUserInfo] = useUserInfoStore(
    useShallow((state) => [state.userInfo, state.setUserInfo, state.resetUserInfo])
  );

  useTwcChatbot();

  useEffect(() => {
    window.globalNavigate = navigate;
  }, [navigate]);

  useEffect(() => {
    const initUserInfo = async () => {
      if (isAuthenticated !== true) {
        resetUserInfo();
        return;
      }

      const decodedJwtValue = await getDecodedJwtValue();

      if (decodedJwtValue) {
        setUserInfo(decodedJwtValue);
      }
    };

    initUserInfo();
  }, [isAuthenticated, setUserInfo]);

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: process.env.GTM_ID ?? '',
      auth: process.env.GTM_AUTH ?? '',
      preview: process.env.GTM_PREVIEW ?? '',
    };

    if (tagManagerArgs.gtmId) {
      TagManager.initialize(tagManagerArgs);
    }
  }, []);

  const isInitializing = isAuthenticated === 'loading' || (isAuthenticated === true && userInfo.loginId === '');
  const isPublicPage = PUBLIC_PATHS.includes(location.pathname);

  if (isInitializing && !isPublicPage) {
    return <LoadingComponent isLoading dimmed />;
  }

  return (
    <>
      <LoadingComponent isLoading={isLoading} dimmed={dimmed} />
      <ReactTooltip />
      {DevToolsFab && (
        <Suspense fallback={null}>
          <DevToolsFab />
        </Suspense>
      )}
      <Routes>
        <Route path={PATH.DELIVERY_AREA_REFINE} element={<DeliveryAreaRefinePage />} />
        <Route element={<LoginLayout />}>
          <Route path={PATH.LOGOUT} element={<LogoutPage />} />
        </Route>
        {isAuthenticated === true && (
          <>
            <Route element={<Layout ownerType={userInfo.ownerType} logisticsTypes={userInfo.logisticsTypes} />}>
              {/* 권한에 따른 라우트 생성 */}
              {getAccessibleRoutes(userInfo.ownerType, userInfo.logisticsTypes).map((route) => (
                <Route key={route.path} path={route.path} element={<route.component />} />
              ))}
            </Route>
            <Route
              path="*"
              element={<Navigate replace to={getDefaultRoute(userInfo.ownerType, userInfo.logisticsTypes)} />}
            />
          </>
        )}
        {isAuthenticated === false && (
          <>
            <Route element={<LoginLayout />}>
              <Route path={PATH.LOGIN} element={<LoginPage />} />
              <Route path={PATH.CHANGE_PASSWORD} element={<ChangePasswordPage />} />
              <Route path={PATH.FIND_ID} element={<FindIdPage />} />
              <Route path={PATH.FIND_PASSWORD} element={<FindPasswordPage />} />
              <Route path={PATH.TWO_FACTOR_AUTH} element={<TwoFactorAuthPage />} />
            </Route>
            <Route path="*" element={<Navigate replace to={PATH.LOGIN} />} />
          </>
        )}
        <Route path="*" element={<Spinner fullscreen />} />
      </Routes>
    </>
  );
};

export default App;
